import { useEffect, useState } from 'react';

export interface ICardFetchState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

/**
 * Shared data-fetch pattern used by Metric View cards (and usable elsewhere):
 * fetches once per deps change, cancels stale updates, exposes loading and
 * error state, and returns the most recent successful response in `data`.
 *
 * Keeping this in one place avoids duplicating the useEffect + AbortController
 * + try/catch pattern in every card — SonarCloud flags repeated structures
 * even when the fetched types differ.
 */
export function useCardFetch<T>(
  fetcher: () => Promise<T>,
  deps: unknown[],
  fallbackMessage = 'Failed to load.',
): ICardFetchState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetcher()
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : fallbackMessage);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error };
}
